import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Brain, TrendingUp, Calendar, Users, CheckCircle2, Download, Share2, Plus, X, Sparkles } from 'lucide-react';
interface BookingBuilderProps {
  propertyId: string;
  onBack: () => void;
}
export function BookingBuilder({
  propertyId,
  onBack
}: BookingBuilderProps) {
  const [rooms, setRooms] = useState(25);
  const [showPackages, setShowPackages] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [needsEventSpace, setNeedsEventSpace] = useState(false);
  const [eventRequests, setEventRequests] = useState([{
    type: 'Reception / Banquet',
    guests: 50,
    setup: 'Banquet',
    date: '',
    notes: ''
  }]);
  const propertyName = propertyId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const handleGenerate = () => {
    setShowPackages(true);
  };
  const addEventRequest = () => {
    setEventRequests([...eventRequests, {
      type: 'Ceremony',
      guests: 50,
      setup: 'Theater',
      date: '',
      notes: ''
    }]);
  };
  const removeEventRequest = (index: number) => {
    setEventRequests(eventRequests.filter((_, i) => i !== index));
  };
  const updateEventRequest = (index: number, field: string, value: string | number) => {
    setEventRequests(eventRequests.map((req, i) => i === index ? {
      ...req,
      [field]: value
    } : req));
  };
  return <div className="space-y-8 animate-in fade-in duration-500 pb-24" data-id="element-30">
      <button onClick={onBack} className="flex items-center text-sm font-medium text-stone-500 hover:text-stone-900 transition-colors" data-id="element-31">
        <ArrowLeft className="w-4 h-4 mr-2" data-id="element-32" />
        Back to Resorts
      </button>

      <div className="flex flex-col lg:flex-row gap-8 items-start" data-id="element-33">
        {/* LEFT: Configuration Form */}
        <div className="w-full lg:w-3/5 space-y-8" data-id="element-34">
          <div data-id="element-35">
            <h1 className="font-serif text-3xl md:text-4xl text-stone-900 mb-2" data-id="element-36">
              Configure Group Booking
            </h1>
            <p className="text-stone-500 font-mono text-sm uppercase tracking-widest" data-id="element-37">
              {propertyName}
            </p>
          </div>

          <div className="bg-white border border-stone-200 rounded-xl p-6 md:p-8 shadow-sm space-y-8" data-id="element-38">
            {/* Group Details */}
            <section data-id="element-39">
              <h2 className="font-serif text-xl text-stone-900 mb-6 border-b border-stone-100 pb-2" data-id="element-40">
                Group Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-41">
                <div className="md:col-span-2" data-id="element-42">
                  <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-43">
                    Group Name
                  </label>
                  <input type="text" placeholder="e.g., Smith-Johnson Wedding" className="w-full p-3 border border-stone-200 rounded-md focus:ring-2 focus:ring-red-600/20 focus:border-red-600 outline-none transition-all" data-id="element-44" />
                </div>
                <div data-id="element-45">
                  <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-46">
                    Event Type
                  </label>
                  <select className="w-full p-3 border border-stone-200 rounded-md focus:ring-2 focus:ring-red-600/20 focus:border-red-600 outline-none transition-all bg-white" data-id="element-47">
                    <option data-id="element-48">Wedding</option>
                    <option data-id="element-49">Corporate Retreat</option>
                    <option data-id="element-50">Conference</option>
                    <option data-id="element-51">Family Reunion</option>
                    <option data-id="element-52">Incentive Trip</option>
                  </select>
                </div>
                <div data-id="element-53">
                  <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-54">
                    Dates
                  </label>
                  <div className="flex items-center border border-stone-200 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-red-600/20 focus-within:border-red-600 transition-all" data-id="element-55">
                    <div className="pl-3 text-stone-400" data-id="element-56">
                      <Calendar className="w-4 h-4" data-id="element-57" />
                    </div>
                    <input type="text" defaultValue="Oct 12 - Oct 16, 2026" className="w-full p-3 outline-none" data-id="element-58" />
                  </div>
                </div>
                <div data-id="element-59">
                  <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-60">
                    Number of Rooms
                  </label>
                  <div className="flex items-center border border-stone-200 rounded-md overflow-hidden" data-id="element-61">
                    <button onClick={() => setRooms(Math.max(10, rooms - 1))} className="px-4 py-3 bg-stone-50 hover:bg-stone-100 text-stone-600 border-r border-stone-200 transition-colors" data-id="element-62">
                      -
                    </button>
                    <input type="number" value={rooms} onChange={e => setRooms(parseInt(e.target.value) || 10)} className="w-full p-3 text-center outline-none font-mono" data-id="element-63" />
                    <button onClick={() => setRooms(rooms + 1)} className="px-4 py-3 bg-stone-50 hover:bg-stone-100 text-stone-600 border-l border-stone-200 transition-colors" data-id="element-64">
                      +
                    </button>
                  </div>
                </div>
                <div data-id="element-65">
                  <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-66">
                    Estimated Guests
                  </label>
                  <div className="flex items-center border border-stone-200 rounded-md bg-stone-50 px-3 py-3" data-id="element-67">
                    <Users className="w-4 h-4 text-stone-400 mr-2" data-id="element-68" />
                    <span className="font-mono text-stone-600" data-id="element-69">
                      ~{rooms * 2} guests
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Preferences */}
            <section data-id="element-70">
              <h2 className="font-serif text-xl text-stone-900 mb-6 border-b border-stone-100 pb-2" data-id="element-71">
                Preferences & Requests
              </h2>
              <div className="space-y-6" data-id="element-72">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-73">
                  <div data-id="element-74">
                    <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-75">
                      Room Category Preference
                    </label>
                    <select className="w-full p-3 border border-stone-200 rounded-md outline-none bg-white" data-id="element-76">
                      <option data-id="element-77">Run of House (Best Value)</option>
                      <option data-id="element-78">Ocean View Guaranteed</option>
                      <option data-id="element-79">Ocean Front Guaranteed</option>
                      <option data-id="element-80">Suites Only</option>
                    </select>
                  </div>
                  <div data-id="element-81">
                    <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-82">
                      All-Inclusive Plan
                    </label>
                    <select className="w-full p-3 border border-stone-200 rounded-md outline-none bg-white" data-id="element-83">
                      <option data-id="element-84">Standard</option>
                      <option data-id="element-85">Premium</option>
                      <option data-id="element-86">VIP</option>
                    </select>
                  </div>
                </div>
                <div data-id="element-87">
                  <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-88">
                    Special Requests / Event Needs
                  </label>
                  <textarea rows={3} placeholder="e.g., Need a welcome reception area for 50 people on the first night..." className="w-full p-3 border border-stone-200 rounded-md focus:ring-2 focus:ring-red-600/20 focus:border-red-600 outline-none transition-all resize-none" data-id="element-89" />
                </div>
              </div>
            </section>

            {/* Event & Venue Requests */}
            <section data-id="element-90">
              <div className="flex items-center justify-between border-b border-stone-100 pb-2 mb-6" data-id="element-91">
                <h2 className="font-serif text-xl text-stone-900" data-id="element-92">
                  Event & Venue Spaces
                </h2>
                <label className="flex items-center gap-3 cursor-pointer select-none" data-id="element-93">
                  <span className="text-sm text-stone-500" data-id="element-94">
                    Need event space?
                  </span>
                  <button type="button" onClick={() => setNeedsEventSpace(!needsEventSpace)} className={`relative w-11 h-6 rounded-full transition-colors ${needsEventSpace ? 'bg-red-600' : 'bg-stone-200'}`} data-id="element-95">
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${needsEventSpace ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-96" />
                  </button>
                </label>
              </div>

              {needsEventSpace && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="space-y-6" data-id="element-97">
                  <p className="text-sm text-stone-500" data-id="element-98">
                    Request venue availability for ceremonies, receptions,
                    meetings, and more. The resort will confirm space and
                    pricing with your quote.
                  </p>

                  {eventRequests.map((event, index) => <div key={index} className="border border-stone-200 rounded-lg p-5 bg-stone-50/50 space-y-4 relative" data-id="element-99">
                      {eventRequests.length > 1 && <button onClick={() => removeEventRequest(index)} className="absolute top-3 right-3 p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-id="element-100">
                          <X className="w-4 h-4" data-id="element-101" />
                        </button>}

                      <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-102">
                        Event {index + 1}
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-103">
                        <div data-id="element-104">
                          <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-105">
                            Event Type
                          </label>
                          <select value={event.type} onChange={e => updateEventRequest(index, 'type', e.target.value)} className="w-full p-3 border border-stone-200 rounded-md outline-none bg-white text-sm" data-id="element-106">
                            <option data-id="element-107">Ceremony</option>
                            <option data-id="element-108">Reception / Banquet</option>
                            <option data-id="element-109">Cocktail Hour</option>
                            <option data-id="element-110">Rehearsal Dinner</option>
                            <option data-id="element-111">Conference / Meeting</option>
                            <option data-id="element-112">Team Building Activity</option>
                            <option data-id="element-113">Awards / Gala Dinner</option>
                            <option data-id="element-114">Other</option>
                          </select>
                        </div>
                        <div data-id="element-115">
                          <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-116">
                            Guest Count
                          </label>
                          <div className="flex items-center border border-stone-200 rounded-md overflow-hidden bg-white" data-id="element-117">
                            <button onClick={() => updateEventRequest(index, 'guests', Math.max(10, (event.guests as number) - 10))} className="px-3 py-3 bg-stone-50 hover:bg-stone-100 text-stone-600 border-r border-stone-200 transition-colors text-sm" data-id="element-118">
                              -
                            </button>
                            <input type="number" value={event.guests} onChange={e => updateEventRequest(index, 'guests', parseInt(e.target.value) || 10)} className="w-full p-3 text-center outline-none font-mono text-sm" data-id="element-119" />
                            <button onClick={() => updateEventRequest(index, 'guests', (event.guests as number) + 10)} className="px-3 py-3 bg-stone-50 hover:bg-stone-100 text-stone-600 border-l border-stone-200 transition-colors text-sm" data-id="element-120">
                              +
                            </button>
                          </div>
                        </div>
                        <div data-id="element-121">
                          <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-122">
                            Preferred Setup
                          </label>
                          <select value={event.setup} onChange={e => updateEventRequest(index, 'setup', e.target.value)} className="w-full p-3 border border-stone-200 rounded-md outline-none bg-white text-sm" data-id="element-123">
                            <option data-id="element-124">Banquet (Round Tables)</option>
                            <option data-id="element-125">Theater (Rows)</option>
                            <option data-id="element-126">Classroom</option>
                            <option data-id="element-127">U-Shape</option>
                            <option data-id="element-128">Cocktail (Standing)</option>
                            <option data-id="element-129">Ceremony (Aisle + Seating)</option>
                            <option data-id="element-130">Custom / TBD</option>
                          </select>
                        </div>
                        <div data-id="element-131">
                          <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-132">
                            Event Date & Time
                          </label>
                          <div className="flex items-center border border-stone-200 rounded-md overflow-hidden bg-white" data-id="element-133">
                            <div className="pl-3 text-stone-400" data-id="element-134">
                              <Calendar className="w-4 h-4" data-id="element-135" />
                            </div>
                            <input type="text" placeholder="e.g., Oct 13, 6:00 PM" value={event.date} onChange={e => updateEventRequest(index, 'date', e.target.value)} className="w-full p-3 outline-none text-sm" data-id="element-136" />
                          </div>
                        </div>
                      </div>

                      <div data-id="element-137">
                        <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-138">
                          Venue Notes
                        </label>
                        <textarea rows={2} placeholder="e.g., Need AV setup, dance floor, outdoor preferred, specific décor needs..." value={event.notes} onChange={e => updateEventRequest(index, 'notes', e.target.value)} className="w-full p-3 border border-stone-200 rounded-md outline-none resize-none bg-white text-sm focus:ring-2 focus:ring-red-600/20 focus:border-red-600 transition-all" data-id="element-139" />
                      </div>
                    </div>)}

                  <button onClick={addEventRequest} className="flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors" data-id="element-140">
                    <Plus className="w-4 h-4" data-id="element-141" />
                    Add Another Event
                  </button>
                </motion.div>}
            </section>

            {!showPackages && <button onClick={handleGenerate} className="w-full py-4 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium text-lg transition-colors shadow-sm flex items-center justify-center gap-2" data-id="element-142">
                <Brain className="w-5 h-5" data-id="element-143" />
                Generate Instant Quote
              </button>}
          </div>
        </div>

        {/* RIGHT: RateBrain & Packages */}
        <div className="w-full lg:w-2/5 space-y-6 sticky top-24" data-id="element-144">
          {/* RateBrain Card */}
          <div className="bg-stone-900 text-stone-50 rounded-xl p-6 shadow-lg relative overflow-hidden" data-id="element-145">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/20 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none" data-id="element-146" />

            <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-147">
              <div className="p-2 bg-red-600/20 rounded-lg" data-id="element-148">
                <Brain className="w-5 h-5 text-red-500" data-id="element-149" />
              </div>
              <h3 className="font-serif text-lg" data-id="element-150">RateBrain Intelligence</h3>
            </div>

            <div className="space-y-6 relative z-10" data-id="element-151">
              <div data-id="element-152">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-400 mb-2" data-id="element-153">
                  Market Demand
                </p>
                <div className="flex items-center gap-2" data-id="element-154">
                  <div className="flex gap-1" data-id="element-155">
                    <div className="w-2 h-4 bg-red-600 rounded-sm" data-id="element-156" />
                    <div className="w-2 h-4 bg-red-600 rounded-sm" data-id="element-157" />
                    <div className="w-2 h-4 bg-red-600 rounded-sm" data-id="element-158" />
                    <div className="w-2 h-4 bg-stone-700 rounded-sm" data-id="element-159" />
                  </div>
                  <span className="text-sm font-medium" data-id="element-160">High Season</span>
                </div>
              </div>

              <div className="pt-4 border-t border-stone-800" data-id="element-161">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-400 mb-2" data-id="element-162">
                  Suggested Base Rate
                </p>
                <div className="flex items-baseline gap-2" data-id="element-163">
                  <span className="font-mono text-3xl text-white" data-id="element-164">$215</span>
                  <span className="text-sm text-stone-400" data-id="element-165">/ room / night</span>
                </div>
                <p className="flex items-center gap-1.5 text-sm text-emerald-400 mt-2" data-id="element-166">
                  <TrendingUp className="w-4 h-4" data-id="element-167" />
                  12% below competitive set average
                </p>
              </div>

              <div className="pt-4 border-t border-stone-800" data-id="element-168">
                <p className="text-sm text-stone-300 flex items-start gap-2" data-id="element-169">
                  <span className="text-red-500 font-bold mt-0.5" data-id="element-170">!</span>
                  Book within 14 days to lock in this optimal rate before
                  seasonal increases.
                </p>
              </div>
            </div>
          </div>

          {/* Packages Display */}
          {showPackages && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="space-y-4" data-id="element-171">
              <h3 className="font-serif text-xl text-stone-900 mb-4" data-id="element-172">
                Available Packages
              </h3>

              {/* Event Space Confirmation */}
              {needsEventSpace && eventRequests.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-id="element-173">
                  <div className="flex items-start gap-3" data-id="element-174">
                    <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-175" />
                    <div data-id="element-176">
                      <p className="text-sm font-medium text-amber-900 mb-1" data-id="element-177">
                        Event Space Request Submitted
                      </p>
                      <p className="text-sm text-amber-700" data-id="element-178">
                        {eventRequests.length} venue
                        {eventRequests.length > 1 ? 's' : ''} requested (
                        {eventRequests.map(e => e.type).join(', ')}). The
                        resort will confirm availability and pricing — typically
                        within 2 hours.
                      </p>
                    </div>
                  </div>
                </div>}

              {/* Better (Recommended) */}
              <div onClick={() => setSelectedPackage('better')} className={`bg-white border-2 rounded-xl p-5 cursor-pointer transition-all ${selectedPackage === 'better' ? 'border-red-600 shadow-md ring-4 ring-red-600/10' : 'border-stone-200 hover:border-red-300'}`} data-id="element-179">
                <div className="flex justify-between items-start mb-2" data-id="element-180">
                  <div data-id="element-181">
                    <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 text-xs font-mono uppercase tracking-widest rounded mb-2" data-id="element-182">
                      Recommended
                    </span>
                    <h4 className="font-serif text-lg text-stone-900" data-id="element-183">
                      Premium Block
                    </h4>
                  </div>
                  <div className="text-right" data-id="element-184">
                    <p className="font-mono text-xl text-stone-900" data-id="element-185">
                      ${(245 * rooms * 4).toLocaleString()}
                    </p>
                    <p className="text-xs text-stone-500" data-id="element-186">Total</p>
                  </div>
                </div>
                <ul className="space-y-2 mt-4" data-id="element-187">
                  <li className="flex items-start gap-2 text-sm text-stone-600" data-id="element-188">
                    <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-189" />
                    <span data-id="element-190">Ocean View Guaranteed</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-stone-600" data-id="element-191">
                    <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-192" />
                    <span data-id="element-193">1 comp room per 20 booked</span>
                  </li>
                </ul>
              </div>

              {/* Good */}
              <div onClick={() => setSelectedPackage('good')} className={`bg-white border rounded-xl p-5 cursor-pointer transition-all ${selectedPackage === 'good' ? 'border-stone-900 shadow-md ring-4 ring-stone-900/10' : 'border-stone-200 hover:border-stone-300'}`} data-id="element-194">
                <div className="flex justify-between items-start mb-2" data-id="element-195">
                  <h4 className="font-serif text-lg text-stone-900" data-id="element-196">
                    Standard Block
                  </h4>
                  <div className="text-right" data-id="element-197">
                    <p className="font-mono text-xl text-stone-900" data-id="element-198">
                      ${(215 * rooms * 4).toLocaleString()}
                    </p>
                    <p className="text-xs text-stone-500" data-id="element-199">Total</p>
                  </div>
                </div>
                <p className="text-sm text-stone-600 mt-2" data-id="element-200">
                  Run of house rooms, standard all-inclusive, 1 comp per 25.
                </p>
              </div>

              {/* Actions */}
              {selectedPackage && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="pt-4 space-y-3" data-id="element-201">
                  <button className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium transition-colors shadow-sm" data-id="element-202">
                    Place 72hr Hold
                  </button>
                  <div className="grid grid-cols-2 gap-3" data-id="element-203">
                    <button className="flex items-center justify-center gap-2 py-2.5 border border-stone-200 bg-white hover:bg-stone-50 text-stone-700 rounded-md text-sm font-medium transition-colors" data-id="element-204">
                      <Download className="w-4 h-4" data-id="element-205" />
                      Proposal
                    </button>
                    <button className="flex items-center justify-center gap-2 py-2.5 border border-stone-200 bg-white hover:bg-stone-50 text-stone-700 rounded-md text-sm font-medium transition-colors" data-id="element-206">
                      <Share2 className="w-4 h-4" data-id="element-207" />
                      Share
                    </button>
                  </div>
                </motion.div>}
            </motion.div>}
        </div>
      </div>
    </div>;
}